import { useEffect, useMemo, useState } from 'react'
import Card from '@components/ui/Card'
import Badge from '@components/ui/Badge'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import Loading from '@components/ui/Loading'
import { Search, X, ArrowUpDown, Pencil, Archive, ArchiveRestore, Trash2, Plus, BarChart3, TrendingUp, Users, DollarSign } from 'lucide-react'
import { listScenarios, listSimulations, updateScenario, deleteScenario } from '@services/scenarioService'
import { useNavigate } from 'react-router-dom'

export default function ScenariosPage() {
  const [scenarios, setScenarios] = useState([])
  const [selected, setSelected] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const selectedList = scenarios.filter(s => selected[s.id])
  const [latestMap, setLatestMap] = useState({})
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all') // all|active|draft|archived
  const [sortKey, setSortKey] = useState('updated') // name|status|updated
  const [sortDir, setSortDir] = useState('desc') // asc|desc

  const pillClass = (active) => `px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${active ? 'bg-primary text-white border-primary shadow-sm' : 'bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-primary/60'}`
  const navigate = useNavigate()

  const handleEdit = (s) => {
    navigate(`/simulador?id=${s.id}`)
  }

  const handleToggleArchive = async (s) => {
    const next = (s.status || '').toLowerCase() === 'archived' ? 'active' : 'archived'
    try {
      await updateScenario(s.id, { status: next })
      // refresh list
      const data = await listScenarios({})
      setScenarios(data)
    } catch (e) {
      alert('Erro ao atualizar cenário: ' + e.message)
    }
  }

  const handleDelete = async (s) => {
    if (!confirm(`Excluir cenário "${s.name}"? Esta ação não pode ser desfeita.`)) return
    try {
      await deleteScenario(s.id)
      setScenarios(prev => prev.filter(x => x.id !== s.id))
    } catch (e) {
      alert('Erro ao excluir: ' + e.message)
    }
  }

  useEffect(() => {
    const abort = new AbortController()
    let active = true
    async function load() {
      try {
        setLoading(true)
        const data = await listScenarios({}, abort.signal)
        if (!active) return
        setScenarios(data)
      } catch (e) {
        if (abort.signal.aborted || e?.name === 'AbortError') return
        if (!active) return
        setError(e.message)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
      abort.abort()
    }
  }, [])

  const toggle = (id) => {
    setSelected((prev) => {
      const keys = Object.keys(prev)
      const next = { ...prev }
      if (next[id]) delete next[id]
      else {
        if (keys.length >= 3) {
          alert('Selecione no máximo 3 cenários para comparar')
          return prev
        }
        next[id] = true
      }
      return next
    })
  }

  useEffect(() => {
    const abort = new AbortController()
    let active = true
    async function loadLatest() {
      try {
        const map = {}
        for (const s of scenarios) {
          if (!selected[s.id]) continue
          const sims = await listSimulations({ scenario: s.id, ordering: '-created_at', page_size: 1 }, abort.signal)
          map[s.id] = sims[0] || null
        }
        if (!active) return
        setLatestMap(map)
      } catch (e) {
        if (abort.signal.aborted || e?.name === 'AbortError') return
      }
    }
    const selectedKeys = Object.keys(selected)
    if (selectedKeys.length > 0) loadLatest()
    else setLatestMap({})
    return () => { active = false; abort.abort() }
  }, [scenarios, selected])

  const filtered = useMemo(() => {
    let data = Array.isArray(scenarios) ? [...scenarios] : []
    if (statusFilter !== 'all') data = data.filter(s => (s.status || '').toLowerCase() === statusFilter)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      data = data.filter(s => (s.name||'').toLowerCase().includes(q) || (s.description||'').toLowerCase().includes(q))
    }
    data.sort((a,b) => {
      let av, bv
      if (sortKey === 'name') { av=(a.name||'').toLowerCase(); bv=(b.name||'').toLowerCase() }
      else if (sortKey === 'status') { av=(a.status||'').toLowerCase(); bv=(b.status||'').toLowerCase() }
      else { av=new Date(a.updated_at||a.created_at||0).getTime(); bv=new Date(b.updated_at||b.created_at||0).getTime() }
      if (av<bv) return sortDir==='asc'?-1:1
      if (av>bv) return sortDir==='asc'?1:-1
      return 0
    })
    return data
  }, [scenarios, statusFilter, searchQuery, sortKey, sortDir])

  if (loading) return <Loading />
  if (error) return <div className="p-4 text-red-600">Erro: {error}</div>

  return (
    <div className="p-4 space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold">Cenários</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Selecione até 3 cenários para comparar lado a lado.</p>
          </div>
          <Button className="flex-shrink-0" onClick={()=> navigate('/simulador')}>
            <Plus className="w-4 h-4 mr-2"/> <span className="hidden sm:inline">Novo Cenário</span><span className="sm:hidden">Novo</span>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Input label="Buscar" placeholder="Nome ou descrição..." value={searchQuery} onChange={(e)=> setSearchQuery(e.target.value)} LeftIcon={Search} />
          </div>
          <Button variant="ghost" size="sm" onClick={()=> setSearchQuery('')} aria-label="Limpar busca" title="Limpar busca" className="self-end"><X className="w-4 h-4"/></Button>

          <div className="hidden md:flex items-center gap-2">
            <span className="text-xs text-gray-500">Status:</span>
            <button className={pillClass(statusFilter==='all')} onClick={()=> setStatusFilter('all')}>Todos</button>
            <button className={pillClass(statusFilter==='active')} onClick={()=> setStatusFilter('active')}>Ativos</button>
            <button className={pillClass(statusFilter==='draft')} onClick={()=> setStatusFilter('draft')}>Rascunhos</button>
            <button className={pillClass(statusFilter==='archived')} onClick={()=> setStatusFilter('archived')}>Arquivados</button>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1 sm:flex-none">
              <label className="label"><span className="label-text text-xs">Ordenar</span></label>
              <select className="select select-bordered select-sm w-full" value={sortKey} onChange={(e)=> setSortKey(e.target.value)}>
                <option value="updated">Atualizado</option>
                <option value="name">Nome</option>
                <option value="status">Status</option>
              </select>
            </div>
            <div className="pb-1">
              <Button variant="ghost" size="sm" onClick={()=> setSortDir(d=> d==='asc'?'desc':'asc')} title={sortDir==='asc'?'Ascendente':'Descendente'} aria-label="Alternar ordem"><ArrowUpDown className="w-4 h-4"/></Button>
            </div>
          </div>

          {selectedList.length > 0 && (
            <Button variant="secondary" onClick={() => {
              document.getElementById('comparison-section')?.scrollIntoView({ behavior: 'smooth' })
            }} className="w-full sm:w-auto">
              <BarChart3 className="w-4 h-4 mr-2"/> Ver Comparação ({selectedList.length})
            </Button>
          )}
        </div>

        <div className="md:hidden flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Status:</span>
          <button className={pillClass(statusFilter==='all')} onClick={()=> setStatusFilter('all')}>Todos</button>
          <button className={pillClass(statusFilter==='active')} onClick={()=> setStatusFilter('active')}>Ativos</button>
          <button className={pillClass(statusFilter==='draft')} onClick={()=> setStatusFilter('draft')}>Rascunhos</button>
          <button className={pillClass(statusFilter==='archived')} onClick={()=> setStatusFilter('archived')}>Arquivados</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((s) => (
          <div key={s.id} className="card card-bordered p-3 flex flex-col gap-3 cursor-pointer" onDoubleClick={() => navigate(`/cenarios/${s.id}`)}>
            <div className="flex items-start gap-3">
              <input type="checkbox" className="mt-1 flex-shrink-0" checked={!!selected[s.id]} onChange={() => toggle(s.id)} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{s.name}</span>
                      {s.status === 'active' && <Badge variant="success">Ativo</Badge>}
                      {s.status === 'draft' && <Badge variant="warning">Rascunho</Badge>}
                      {s.status === 'archived' && <Badge variant="secondary">Arquivado</Badge>}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{s.description || '—'}</p>
                    <div className="text-sm mt-1 text-gray-700 flex gap-4">
                      <span>Base: {s.calculation_base}</span>
                      <span>Ref: {s.reference_month || '—'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" aria-label="Editar" title="Editar" onClick={()=> handleEdit(s)}>
                      <Pencil className="w-4 h-4"/>
                    </Button>
                    <Button variant={s.status==='archived'?'success':'outline'} size="sm" aria-label={s.status==='archived'?'Reativar':'Arquivar'} title={s.status==='archived'?'Reativar':'Arquivar'} onClick={()=> handleToggleArchive(s)}>
                      {s.status==='archived'? <ArchiveRestore className="w-4 h-4"/> : <Archive className="w-4 h-4"/>}
                    </Button>
                    <Button variant="danger" size="sm" aria-label="Excluir" title="Excluir" onClick={()=> handleDelete(s)}>
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedList.length > 0 && (
        <div id="comparison-section" className="space-y-6">
          {/* Header da Comparação */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">Comparação de Cenários</h3>
              <p className="text-gray-600 dark:text-gray-400">Compare até 3 cenários lado a lado</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setSelected({})}
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Limpar Seleção
            </Button>
          </div>

          {/* Comparação Lado a Lado */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {selectedList.map((s, idx) => {
              const latest = latestMap[s.id]
              const layout = s.layout_json || {}
              const appliedRules = layout.applied_rules || []
              const simulationTotals = layout.simulation_totals || {}

              return (
                <Card key={s.id} className="relative">
                  {/* Badge de posição */}
                  <div className="absolute -top-2 -right-2">
                    <Badge variant="primary" className="text-xs">#{idx + 1}</Badge>
                  </div>

                  <div className="space-y-4">
                    {/* Header */}
                    <div>
                      <h4 className="font-semibold text-lg truncate">{s.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{s.description || '—'}</p>
                      <div className="flex items-center gap-2 mt-2">
                        {s.status === 'active' && <Badge variant="success">Ativo</Badge>}
                        {s.status === 'draft' && <Badge variant="warning">Rascunho</Badge>}
                        {s.status === 'archived' && <Badge variant="secondary">Arquivado</Badge>}
                      </div>
                    </div>

                    {/* Configurações */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Base:</span>
                        <span className="font-medium">{s.calculation_base}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Mês Ref.:</span>
                        <span className="font-medium">{s.reference_month || '—'}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Regras:</span>
                        <span className="font-medium">{appliedRules.length}</span>
                      </div>
                    </div>

                    {/* Métricas */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Funcionários</p>
                          <p className="font-semibold">{simulationTotals.affected_employees?.toLocaleString('pt-BR') || '—'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Folha Base</p>
                          <p className="font-semibold">R$ {simulationTotals.current_payroll?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-orange-600" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Total</p>
                          <p className="font-semibold text-green-600">+R$ {simulationTotals.total_increase?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-purple-600" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Salário Bruto</p>
                          <p className="font-semibold text-purple-600">R$ {simulationTotals.new_payroll?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Impacto Percentual */}
                    {simulationTotals.percentage_increase && (
                      <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg p-3 border border-orange-200 dark:border-orange-800">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-orange-900 dark:text-orange-200">Variação</span>
                          <span className="text-lg font-bold text-orange-600">
                            +{simulationTotals.percentage_increase.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Última simulação */}
                    <div className="text-xs text-gray-500 border-t pt-2">
                      Última atualização: {latest ? new Date(latest.created_at).toLocaleString('pt-BR') : new Date(s.updated_at || s.created_at).toLocaleString('pt-BR')}
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>

          {/* Comparação Detalhada */}
          {selectedList.length >= 2 && (
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
              <div className="space-y-6">
                <h4 className="text-lg font-semibold flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Comparação Detalhada
                </h4>

                {/* Resumo Geral */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {selectedList.map(s => {
                    const layout = s.layout_json || {}
                    const simulationTotals = layout.simulation_totals || {}
                    return (
                      <div key={s.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                        <h5 className="font-medium text-sm mb-3 text-center">{s.name}</h5>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Funcionários:</span>
                            <span className="font-medium">{simulationTotals.affected_employees?.toLocaleString('pt-BR') || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Folha Base:</span>
                            <span className="font-medium">R$ {simulationTotals.current_payroll?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Total:</span>
                            <span className="font-medium text-green-600">+R$ {simulationTotals.total_increase?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Salário Bruto:</span>
                            <span className="font-medium text-purple-600">R$ {simulationTotals.new_payroll?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Variação:</span>
                            <span className="font-medium text-orange-600">+{simulationTotals.percentage_increase?.toFixed(2) || '—'}%</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Diferenças */}
                <div className="space-y-4">
                  <h5 className="font-medium text-sm text-gray-700 dark:text-gray-300">Diferenças de Impacto</h5>
                  {selectedList.length === 2 && (() => {
                    const s1 = selectedList[0]
                    const s2 = selectedList[1]
                    const impact1 = (s1.layout_json?.simulation_totals?.total_increase || 0)
                    const impact2 = (s2.layout_json?.simulation_totals?.total_increase || 0)
                    const diff = impact2 - impact1
                    const percentDiff = impact1 > 0 ? ((diff / impact1) * 100) : 0

                    return (
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <h6 className="font-medium text-sm mb-2">{s1.name} vs {s2.name}</h6>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span>Diferença Absoluta:</span>
                                <span className={`font-medium ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                  {diff > 0 ? '+' : ''}R$ {Math.abs(diff).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Diferença Percentual:</span>
                                <span className={`font-medium ${percentDiff > 0 ? 'text-green-600' : percentDiff < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                  {percentDiff > 0 ? '+' : ''}{percentDiff.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <h6 className="font-medium text-sm mb-2">Comparação de Funcionários</h6>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span>{s1.name}:</span>
                                <span className="font-medium">{(s1.layout_json?.simulation_totals?.affected_employees || 0).toLocaleString('pt-BR')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>{s2.name}:</span>
                                <span className="font-medium">{(s2.layout_json?.simulation_totals?.affected_employees || 0).toLocaleString('pt-BR')}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })()}
                  {selectedList.length === 3 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                      <h6 className="font-medium text-sm mb-2">Comparação entre os 3 Cenários</h6>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        {selectedList.map((s, idx) => {
                          const impact = s.layout_json?.simulation_totals?.total_increase || 0
                          const maxImpact = Math.max(...selectedList.map(s => s.layout_json?.simulation_totals?.total_increase || 0))
                          const isMax = impact === maxImpact
                          return (
                            <div key={s.id} className={`text-center p-2 rounded ${isMax ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-50 dark:bg-gray-800'}`}>
                              <div className="font-medium text-xs">{s.name}</div>
                              <div className={`text-lg font-bold ${isMax ? 'text-green-600' : 'text-gray-600'}`}>
                                R$ {impact.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </div>
                              {isMax && <div className="text-xs text-green-600">Maior impacto</div>}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Detalhes das Regras */}
                <div className="space-y-4">
                  <h5 className="font-medium text-sm text-gray-700 dark:text-gray-300">Detalhes das Regras Aplicadas</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {selectedList.map(s => {
                      const appliedRules = s.layout_json?.applied_rules || []
                      return (
                        <div key={s.id} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                          <h6 className="font-medium text-sm mb-3">{s.name} ({appliedRules.length} regras)</h6>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {appliedRules.map((rule, idx) => (
                              <div key={idx} className="text-xs border-b border-gray-200 dark:border-gray-700 pb-2">
                                <div className="font-medium truncate">{rule.rule_id ? `Regra ${rule.rule_id}` : 'Regra'}</div>
                                <div className="text-gray-600 dark:text-gray-400">
                                  +R$ {rule.estimated_increase?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) || '—'} 
                                  ({rule.percentage_impact?.toFixed(1) || '—'}%)
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Status e Configurações */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <h5 className="font-medium text-sm mb-3">Status dos Cenários</h5>
                    <div className="space-y-2">
                      {selectedList.map(s => (
                        <div key={s.id} className="flex justify-between items-center">
                          <span className="text-sm truncate mr-2">{s.name}:</span>
                          <Badge variant={s.status === 'active' ? 'success' : s.status === 'draft' ? 'warning' : 'secondary'} className="text-xs">
                            {s.status === 'active' ? 'Ativo' : s.status === 'draft' ? 'Rascunho' : 'Arquivado'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <h5 className="font-medium text-sm mb-3">Configurações</h5>
                    <div className="space-y-2 text-sm">
                      {selectedList.map(s => (
                        <div key={s.id}>
                          <div className="font-medium text-xs mb-1">{s.name}:</div>
                          <div className="text-gray-600 dark:text-gray-400 text-xs">
                            Base: {s.calculation_base} | Mês: {s.reference_month || '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
